import Picker from '@emoji-mart/react'
import theme from '@globals/themes/defaultTheme'
import { tabletQuery } from '@globals/themes/themeConstants'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import SentimentSatisfiedOutlined from '@mui/icons-material/SentimentSatisfiedOutlined'
import Button from '@mui/material/Button'
import Popper from '@mui/material/Popper'
import Tooltip from '@mui/material/Tooltip'
import useMediaQuery from '@mui/material/useMediaQuery'
import { T, useT } from '@transifex/react'
import { $createTextNode, $getRoot, $getSelection, $isRangeSelection } from 'lexical'
import { useCallback, useState } from 'react'
import { FILTERED_EMOJIS } from './FilteredEmojis.const'

type Emoji = {
  id: string
  name: string
  native: string
  unified: string
  keywords: string[]
  shortcodes: string
  aliases: string[]
}

type EmojiPluginProps = {
  id: string
}

const EmojiPlugin: React.FC<EmojiPluginProps> = ({ id }) => {
  const t = useT()
  const [showPicker, setShowPicker] = useState<boolean>(false)
  const isTablet = useMediaQuery(tabletQuery)
  const [editor] = useLexicalComposerContext()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleEmojiSelect = useCallback(
    (emoji: Emoji) => {
      editor.focus()
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          const textNode = $createTextNode(emoji.native)
          selection.insertNodes([textNode])
        } else {
          const root = $getRoot()
          root.append($createTextNode(emoji.native))
        }
      })
      if (!!showPicker) {
        setShowPicker(false)
      }
    },
    [editor],
  )

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
    setShowPicker(prev => !prev)
  }

  return (
    !isTablet && (
      <>
        <Tooltip id={`tooltipEmojiPlugin-${id}`} placement="top" title={<T _str="Add emoji" />}>
          <Button
            id={`btnEmojiPlugin-${id}`}
            aria-label={t('Add emoji')}
            aria-labelledby={`tooltipEmojiPlugin-${id}`}
            onClick={handleButtonClick}>
            <SentimentSatisfiedOutlined />
          </Button>
        </Tooltip>
        {anchorEl && showPicker && (
          <Popper
            open={showPicker}
            anchorEl={anchorEl}
            placement="auto-start"
            disablePortal
            sx={{ zIndex: theme.zIndex.modal + 1 }}>
            <Picker
              set="facebook"
              onEmojiSelect={handleEmojiSelect}
              onClickOutside={() => setShowPicker(false)}
              previewPosition={isTablet ? 'none' : 'bottom'}
              perLine={isTablet ? 6 : 8}
              exceptEmojis={FILTERED_EMOJIS}
            />
          </Popper>
        )}
      </>
    )
  )
}

export default EmojiPlugin
